/**
 * Product Service (Frontend)
 * 
 * API client for product catalog operations.
 * Used by the Context System to fetch and manage product data.
 */

import { getApiBaseUrl } from '../config/environment';
import { Product } from '../contexts/StudioContext';

const API_BASE = getApiBaseUrl();

export interface ListProductsResponse {
  products: Product[];
  total: number;
  clientId?: string;
}

export interface ProductImportResult {
  imported: number;
  errors: string[];
}

export interface ListClientsResponse {
  clients: string[];
}

interface ListProductsOptions {
  search?: string;
  classification?: string;
  availability?: string;
  limit?: number;
  clientId?: string;
  tenantId?: string;
}

export class ProductService {
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private appendTenantId(queryParams: URLSearchParams, tenantId?: string) {
    if (tenantId) {
      queryParams.append('tenantId', tenantId);
    }
  }

  private buildUrl(path: string, queryParams?: URLSearchParams): string {
    const queryString = queryParams?.toString();
    return `${API_BASE}${path}${queryString ? `?${queryString}` : ''}`;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  /**
   * List tenant-owned products (optionally filtered)
   */
  async listProducts(options?: ListProductsOptions): Promise<ListProductsResponse> {
    const queryParams = new URLSearchParams();
    if (options?.search) queryParams.append('search', options.search);
    if (options?.classification) queryParams.append('classification', options.classification);
    if (options?.availability) queryParams.append('availability', options.availability);
    if (options?.limit) queryParams.append('limit', options.limit.toString());
    if (options?.clientId) queryParams.append('clientId', options.clientId);
    this.appendTenantId(queryParams, options?.tenantId);

    const url = this.buildUrl('/api/products', queryParams);

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list products' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get a single product by ID
   */
  async getProduct(productId: string, tenantId?: string): Promise<Product> {
    const queryParams = new URLSearchParams();
    this.appendTenantId(queryParams, tenantId);
    const url = this.buildUrl(`/api/products/${encodeURIComponent(productId)}`, queryParams);

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get product' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Import products from scraper JSON (tenant-owned; optionally scoped to a clientId)
   */
  async importProducts(
    products: any[],
    clientId?: string,
    tenantId?: string
  ): Promise<ProductImportResult> {
    const queryParams = new URLSearchParams();
    if (clientId) queryParams.append('clientId', clientId);
    this.appendTenantId(queryParams, tenantId);
    const url = this.buildUrl('/api/product-import', queryParams);

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ products }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Delete all products for the tenant
   */
  async deleteAllProducts(tenantId?: string): Promise<number> {
    const queryParams = new URLSearchParams();
    this.appendTenantId(queryParams, tenantId);
    const url = this.buildUrl('/api/products', queryParams);

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete all products' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.deleted as number;
  }

  /**
   * Delete a product by ID
   */
  async deleteProduct(productId: string, tenantId?: string): Promise<void> {
    const queryParams = new URLSearchParams();
    this.appendTenantId(queryParams, tenantId);
    const url = this.buildUrl(`/api/products/${encodeURIComponent(productId)}`, queryParams);

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete product' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
  }

  /**
   * List distinct client IDs for the tenant
   */
  async listClients(tenantId?: string): Promise<string[]> {
    const queryParams = new URLSearchParams();
    this.appendTenantId(queryParams, tenantId);
    const url = this.buildUrl('/api/products-clients', queryParams);

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list clients' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data: ListClientsResponse = await response.json();
    return data.clients;
  }
}

// Export singleton instance
export const productService = new ProductService();

